/**
 * LiveTranscriptionPanel — real-time Spanish caption + German translation
 * panel for the Whereby live classroom.
 *
 * Architecture: see /app/backend/routes/transcription.py
 *  - Teacher's browser captures their mic via Web Speech API (Chrome/Edge).
 *  - Each final Spanish phrase is sent over a WebSocket to the backend.
 *  - The backend translates ES→DE with Claude Sonnet 4.5 and broadcasts to
 *    every connected student.
 *  - Both sides see colour-coded captions: Spanish (blue) + German (orange).
 *
 * Browser support: SpeechRecognition is Chrome/Edge only. We render a friendly
 * banner on unsupported browsers explaining what they're missing.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Microphone,
  MicrophoneSlash,
  Trash,
  CaretDown,
  CaretUp,
  CircleNotch,
  WarningCircle,
  Translate,
  Users,
} from '@phosphor-icons/react';
import { Button } from './ui/button';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Derive ws(s):// URL from the configured https:// backend.
const wsBase = () => BACKEND.replace(/^http/i, 'ws');

const SpeechRecognitionCtor =
  (typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)) || null;

const LiveTranscriptionPanel = ({ roomId = 'habla-main', isTeacher = false, onClose }) => {
  const [connected, setConnected] = useState(false);
  const [listening, setListening] = useState(false);
  const [captions, setCaptions] = useState([]); // {id, es, de, ts, from, from_role}
  const [interim, setInterim] = useState('');
  const [participants, setParticipants] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [error, setError] = useState('');
  const [translating, setTranslating] = useState(false);
  const [pipelineOk, setPipelineOk] = useState(true);
  // Which language the teacher is currently speaking in. Toggles the STT
  // recognition language AND tells the backend how to route the translation
  // (es→de or de→es). Persisted so a mid-class refresh keeps the choice.
  const [sourceLang, setSourceLang] = useState(() => {
    try { return localStorage.getItem('habla.caption.sourceLang') || 'es'; } catch { return 'es'; }
  });
  useEffect(() => {
    try { localStorage.setItem('habla.caption.sourceLang', sourceLang); } catch { /* noop */ }
  }, [sourceLang]);

  // Probe backend once so the teacher immediately knows if the LLM key is
  // missing in the current environment — otherwise the first caption would
  // silently arrive without a German translation (the original prod bug).
  useEffect(() => {
    let cancelled = false;
    if (!isTeacher) return;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/transcription/health`, { credentials: 'include' });
        const json = await res.json();
        if (!cancelled) setPipelineOk(!!json.ok);
      } catch {
        if (!cancelled) setPipelineOk(true); // don't block on a transient fetch error
      }
    })();
    return () => { cancelled = true; };
  }, [isTeacher]);

  const wsRef = useRef(null);
  const recognitionRef = useRef(null);
  const captionListRef = useRef(null);
  // Auto-restart guard — Chrome auto-stops SpeechRecognition every ~60s of
  // silence; we transparently restart unless the teacher explicitly stopped.
  const wantListeningRef = useRef(false);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  const sttSupported = !!SpeechRecognitionCtor;

  // ── WebSocket connection ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      try {
        const url = `${wsBase()}/api/ws/transcription/${encodeURIComponent(roomId)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) { ws.close(); return; }
          setConnected(true);
          setError('');
          reconnectAttemptsRef.current = 0;
        };

        ws.onmessage = (evt) => {
          let msg;
          try { msg = JSON.parse(evt.data); } catch { return; }
          if (msg.type === 'joined') {
            const hist = (msg.history || []).map((c, i) => ({ ...c, id: `h-${i}-${c.ts}` }));
            setCaptions(hist);
            setParticipants(msg.participants || []);
            setTranslating(false);
          } else if (msg.type === 'caption') {
            setCaptions(prev => [...prev, { ...msg, id: `c-${msg.ts}-${prev.length}` }].slice(-200));
            setTranslating(false);
          } else if (msg.type === 'participants') {
            setParticipants(msg.participants || []);
          } else if (msg.type === 'cleared') {
            setCaptions([]);
          } else if (msg.type === 'error') {
            setError(msg.message || 'Unbekannter Fehler');
          }
        };

        ws.onclose = (ev) => {
          setConnected(false);
          if (ev.code === 4401) {
            setError('Nicht authentifiziert');
            return;
          }
          if (cancelled) return;
          // Exponential backoff reconnect, max 5 attempts.
          if (reconnectAttemptsRef.current < 5) {
            const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 10000);
            reconnectAttemptsRef.current += 1;
            reconnectTimerRef.current = setTimeout(connect, delay);
          } else {
            setError('Verbindung verloren');
          }
        };

        ws.onerror = () => {
          // ignored — close handler will retry
        };
      } catch (e) {
        setError('Verbindungsfehler');
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, [roomId]);

  // ── Auto-scroll caption list ─────────────────────────────
  useEffect(() => {
    const el = captionListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [captions.length, interim]);

  // ── Web Speech API (teacher only) ────────────────────────
  const startListening = async () => {
    if (!SpeechRecognitionCtor) {
      setError('Spracherkennung in diesem Browser nicht unterstützt. Bitte Chrome oder Edge nutzen.');
      return;
    }
    if (recognitionRef.current) return;
    // First explicitly request microphone permission so the teacher sees a
    // clear browser prompt. Without this step, the SpeechRecognition API
    // sometimes silently fails when the Whereby iframe is also using the
    // mic on the same tab — the prompt never appears and the teacher
    // doesn't know why captions don't start.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // We don't need to keep the stream — Web Speech API will open its own.
      // Closing it immediately frees the device for the SpeechRecognition
      // engine without conflicting with Whereby (Chrome shares the device
      // between multiple consumers).
      stream.getTracks().forEach(t => { try { t.stop(); } catch { /* noop */ } });
    } catch (e) {
      const name = e?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setError('Mikrofon-Zugriff blockiert. Klicke auf das Schloss-Symbol in der Adressleiste und erlaube den Zugriff für hablaconalberto.de — danach Seite neu laden.');
      } else if (name === 'NotFoundError') {
        setError('Kein Mikrofon gefunden. Bitte ein Mikrofon anschließen.');
      } else if (name === 'NotReadableError') {
        setError('Mikrofon ist von einer anderen App belegt. Schließe andere Programme (z. B. Zoom) und versuche es erneut.');
      } else {
        setError(`Mikrofon-Zugriff fehlgeschlagen: ${name || 'unbekannter Fehler'}`);
      }
      return;
    }
    const rec = new SpeechRecognitionCtor();
    rec.lang = sourceLang === 'de' ? 'de-DE' : 'es-ES';
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        const text = res[0]?.transcript || '';
        if (res.isFinal) {
          const clean = text.trim();
          if (clean) sendCaption(clean);
        } else {
          interimText += text;
        }
      }
      setInterim(interimText.trim());
    };

    rec.onerror = (e) => {
      // 'no-speech', 'aborted', 'audio-capture' are normal — ignore.
      if (e.error && e.error !== 'no-speech' && e.error !== 'aborted') {
        setError(`Mikrofon-Fehler: ${e.error}`);
      }
    };

    rec.onend = () => {
      // Auto-restart if the user is still meant to be transcribing.
      if (wantListeningRef.current) {
        try { rec.start(); } catch { /* already started */ }
      } else {
        recognitionRef.current = null;
        setListening(false);
        setInterim('');
      }
    };

    try {
      rec.start();
      recognitionRef.current = rec;
      wantListeningRef.current = true;
      setListening(true);
      setError('');
    } catch (e) {
      setError('Mikrofon konnte nicht gestartet werden');
    }
  };

  const stopListening = () => {
    wantListeningRef.current = false;
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    recognitionRef.current = null;
    setListening(false);
    setInterim('');
  };

  const sendCaption = (text) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: 'caption', text, source_lang: sourceLang }));
      setTranslating(true);
    } catch { /* noop */ }
  };

  const clearCaptions = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (!window.confirm('Alle Untertitel löschen?')) return;
    try { ws.send(JSON.stringify({ type: 'clear' })); } catch { /* noop */ }
  };

  // ── Cleanup on unmount ──────────────────────────────────
  useEffect(() => () => {
    wantListeningRef.current = false;
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
  }, []);

  const teacherCount = useMemo(() => participants.filter(p => p.is_teacher).length, [participants]);

  return (
    <div
      className="bg-[#1a1a2e] rounded-2xl border border-white/10 flex flex-col overflow-hidden"
      data-testid="live-transcription-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-2 bg-gradient-to-r from-[#1a1a2e] to-[#16213e]">
        <div className="flex items-center gap-2 min-w-0">
          <Translate size={20} weight="duotone" className="text-[#EAB308] flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm leading-tight truncate">Live-Untertitel</p>
            <p className="text-white/50 text-[11px] leading-tight">
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${connected ? 'bg-green-400' : 'bg-red-400 animate-pulse'}`} />
              {connected ? `Verbunden · ${participants.length} hier` : 'Verbinde…'}
              {teacherCount > 0 && connected && <span className="ml-1">· {teacherCount} Lehrer</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => setCollapsed(c => !c)}
            className="p-1.5 rounded-md hover:bg-white/10 text-white/70"
            aria-label={collapsed ? 'Erweitern' : 'Einklappen'}
            data-testid="transcription-toggle-collapse"
          >
            {collapsed ? <CaretDown size={16} /> : <CaretUp size={16} />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Teacher controls */}
          {isTeacher && (
            <div className="px-4 py-3 border-b border-white/10 bg-white/[0.02]" data-testid="teacher-transcription-controls">
              {!sttSupported ? (
                <div className="flex items-start gap-2 text-amber-300 text-xs">
                  <WarningCircle size={16} weight="duotone" className="flex-shrink-0 mt-0.5" />
                  <p>Spracherkennung nicht verfügbar — bitte Chrome oder Edge nutzen, um Live-Untertitel zu senden.</p>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Language toggle — decides which language the teacher is
                      speaking in and therefore whether the backend translates
                      ES→DE (default) or DE→ES. Disabled while listening so a
                      mid-utterance flip cannot corrupt the caption stream. */}
                  <div className="inline-flex rounded-full border border-white/20 overflow-hidden text-[11px] font-semibold" data-testid="transcription-source-lang">
                    <button
                      type="button"
                      disabled={listening}
                      onClick={() => setSourceLang('es')}
                      className={`px-2.5 py-1.5 transition-colors ${
                        sourceLang === 'es'
                          ? 'bg-sky-500 text-white'
                          : 'bg-transparent text-white/70 hover:bg-white/10'
                      } ${listening ? 'opacity-60 cursor-not-allowed' : ''}`}
                      data-testid="transcription-lang-es"
                      title={listening ? 'Erst Mikro stoppen' : 'Ich spreche Spanisch → Deutsch'}
                    >
                      ES → DE
                    </button>
                    <button
                      type="button"
                      disabled={listening}
                      onClick={() => setSourceLang('de')}
                      className={`px-2.5 py-1.5 transition-colors ${
                        sourceLang === 'de'
                          ? 'bg-amber-500 text-white'
                          : 'bg-transparent text-white/70 hover:bg-white/10'
                      } ${listening ? 'opacity-60 cursor-not-allowed' : ''}`}
                      data-testid="transcription-lang-de"
                      title={listening ? 'Erst Mikro stoppen' : 'Ich spreche Deutsch → Spanisch'}
                    >
                      DE → ES
                    </button>
                  </div>
                  {listening ? (
                    <Button
                      onClick={stopListening}
                      size="sm"
                      className="bg-red-500 hover:bg-red-600 text-white h-8 px-3 text-xs"
                      data-testid="transcription-stop-btn"
                    >
                      <MicrophoneSlash size={14} className="mr-1.5" weight="fill" />
                      Mikro stoppen
                    </Button>
                  ) : (
                    <Button
                      onClick={startListening}
                      size="sm"
                      className="bg-[#16A34A] hover:bg-[#15803D] text-white h-8 px-3 text-xs"
                      data-testid="transcription-start-btn"
                    >
                      <Microphone size={14} className="mr-1.5" weight="fill" />
                      Untertitel starten
                    </Button>
                  )}
                  <button
                    onClick={clearCaptions}
                    className="text-white/60 hover:text-white text-xs flex items-center gap-1 px-2 py-1.5 rounded hover:bg-white/10"
                    data-testid="transcription-clear-btn"
                  >
                    <Trash size={12} /> Verlauf löschen
                  </button>
                  {listening && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-red-300">
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      Aufnahme läuft
                    </span>
                  )}
                </div>
              )}
              <p className="text-[10px] text-white/40 mt-2 leading-snug">
                Tipp: Sprich klar und mache kurze Pausen — Pausen werden als Satzende erkannt und übersetzt.
              </p>
              {!pipelineOk && (
                <div className="mt-2 bg-red-500/15 border border-red-500/40 rounded-lg p-2 flex items-start gap-2" data-testid="translation-pipeline-warning">
                  <WarningCircle size={14} weight="fill" className="text-red-300 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-red-200 leading-snug">
                    Der Übersetzungsdienst ist nicht konfiguriert (LLM-Schlüssel fehlt in der Umgebung).
                    Untertitel werden nur auf Spanisch angezeigt, bis das behoben ist.
                  </p>
                </div>
              )}
              <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[10px] text-white/40 leading-snug max-w-[260px]">
                  Funktioniert das Mikro nicht? Öffne die Übersetzung in einem separaten Fenster — dann teilt sie sich nicht das Mikro mit Whereby.
                </p>
                <button
                  onClick={() => {
                    try {
                      window.open(`/live-translate?room=${encodeURIComponent(roomId)}`, 'habla-translate', 'noopener,width=420,height=720');
                    } catch { /* popup blocked */ }
                  }}
                  className="text-[11px] text-emerald-300 font-semibold hover:text-emerald-200 underline whitespace-nowrap"
                  data-testid="transcription-popout-btn"
                >
                  Separates Fenster öffnen
                </button>
              </div>
            </div>
          )}

          {/* Captions list */}
          <div
            ref={captionListRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[300px] max-h-[60vh]"
            data-testid="caption-list"
          >
            {captions.length === 0 && !interim && (
              <div className="h-full flex flex-col items-center justify-center text-center py-10 px-2">
                <Translate size={36} weight="duotone" className="text-white/20 mb-3" />
                <p className="text-white/50 text-sm font-medium">
                  {isTeacher
                    ? sttSupported
                      ? '„Untertitel starten" drücken und sprechen — die Übersetzung erscheint hier.'
                      : 'Untertitel sind in diesem Browser deaktiviert.'
                    : 'Warte, bis Alberto mit dem Sprechen beginnt…'}
                </p>
                {!isTeacher && (
                  <p className="text-white/30 text-xs mt-2">
                    Sobald Alberto spricht, siehst du den spanischen Text (blau) und sofort die deutsche Übersetzung (gold).
                  </p>
                )}
              </div>
            )}

            {captions.map((c) => {
              const deFailed = c.de_status && c.de_status !== 'ok';
              const deMissing = deFailed || !c.de || c.de === c.es;
              return (
                <div key={c.id} className="space-y-1" data-testid="caption-item">
                  {/* Spanish (blue) */}
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-sky-300/80 mt-0.5 flex-shrink-0 w-6">ES</span>
                    <p className="text-sky-300 text-sm leading-snug flex-1">{c.es}</p>
                  </div>
                  {/* German (gold/orange) or explicit failure notice */}
                  {!deMissing ? (
                    <div className="flex items-start gap-2">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-amber-300/80 mt-0.5 flex-shrink-0 w-6">DE</span>
                      <p className="text-amber-200 text-sm leading-snug flex-1">{c.de}</p>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2" data-testid="caption-de-missing">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-red-300/80 mt-0.5 flex-shrink-0 w-6">DE</span>
                      <p className="text-red-300/80 text-xs italic leading-snug flex-1">
                        {c.de_status === 'no_key'
                          ? 'Übersetzungsdienst nicht konfiguriert (LLM-Key fehlt).'
                          : 'Übersetzung fehlgeschlagen — versuche es erneut.'}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Interim Spanish (teacher's current sentence, not yet final) */}
            {interim && (
              <div className="flex items-start gap-2 opacity-60 italic" data-testid="caption-interim">
                <span className="text-[10px] uppercase tracking-wider font-bold text-sky-300/60 mt-0.5 flex-shrink-0 w-6">ES</span>
                <p className="text-sky-300/80 text-sm leading-snug flex-1">{interim}…</p>
              </div>
            )}

            {translating && (
              <div className="flex items-center gap-2 text-white/40 text-xs" data-testid="caption-translating">
                <CircleNotch size={12} className="animate-spin" />
                Übersetze…
              </div>
            )}
          </div>

          {error && (
            <div className="px-4 py-2 border-t border-red-500/30 bg-red-500/10 text-red-300 text-xs flex items-center gap-2" data-testid="transcription-error">
              <WarningCircle size={14} weight="duotone" />
              <span className="flex-1">{error}</span>
            </div>
          )}

          <div className="px-4 py-2 border-t border-white/10 bg-black/30 text-white/40 text-[10px] flex items-center gap-2">
            <Users size={11} />
            <span>
              Untertitel laufen lokal — bitte erlaube dem Browser den Zugriff auf das Mikrofon, wenn du Lehrer bist.
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default LiveTranscriptionPanel;
